import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiClientConfig } from '../api-client-config';
import {
  JobStatusRequest,
  JobStatusResponse,
  WorkspaceItemType,
} from '@primatoo-ms-organization';
import { ApiClientUtils } from '../../api-client.utils';
import {
  DocumentEditorType,
  DownloadFormat,
  DownloadResponse,
  TemplateDocumentType,
} from '@pt/pt-models';
import { AppAssessmentsService } from './documents/app-assessments.service';
import { AiAssessmentsService } from './documents/ai-assessments.service';
import { TreatmentService } from './documents/treatment.service';
import { GlobalMeasureChecksService } from './documents/global-measure-checks.service';
import { CovidAssessmentsService } from './documents/covid-assessments.service';
import * as FileSaver from 'file-saver';
import { SubmitDownloadRequest } from 'it/primatoo/openapi/api/org/schemas/common/docs/SubmitDownloadRequest';
import { SubmitDownloadResponse } from 'it/primatoo/openapi/api/org/schemas/common/docs/SubmitDownloadResponse';

@Injectable({
  providedIn: 'root',
})
export class DownloadService {
  private readonly endpointV1: string;
  private readonly endpointV2: string;

  constructor(
    @Inject(ApiClientConfig) private config,
    private treatmentService: TreatmentService,
    private appAssessmentService: AppAssessmentsService,
    private aiAssessmentService: AiAssessmentsService,
    private globalMeasureChecksService: GlobalMeasureChecksService,
    private covidAssessmentsService: CovidAssessmentsService,
    private httpClient: HttpClient
  ) {
    this.endpointV1 = this.config.endPoint + '/api/org/';
    this.endpointV2 = this.config.endPoint + '/api/org/v2/';
  }

  public findAllJobs(request: JobStatusRequest): Observable<JobStatusResponse> {
    const url = this.endpointV1 + `jobs/status`;
    return this.httpClient.post<JobStatusResponse>(url, request);
  }

  public getDocumentEditorTypeService(type: DocumentEditorType) {
    switch (type) {
      case DocumentEditorType.AI_ASSESSMENT:
        return this.aiAssessmentService;
      case DocumentEditorType.APP_ASSESSMENT:
        return this.appAssessmentService;
      case DocumentEditorType.GLOBAL_MEASURE_CHECK:
        return this.globalMeasureChecksService;
      case DocumentEditorType.TREATMENT:
        return this.treatmentService;
      case DocumentEditorType.COVID_ASSESSMENT:
        return this.covidAssessmentsService;
      default:
    }
    return undefined;
  }

  public getWorkspaceItemTypeService(type: WorkspaceItemType) {
    switch (type) {
      case WorkspaceItemType.AI_ASSESSMENT:
      case WorkspaceItemType.AI_ASSESSMENT_VALIDATED:
      case WorkspaceItemType.AI_ASSESSMENT_SNAPSHOT:
        return this.aiAssessmentService;

      case WorkspaceItemType.APP_ASSESSMENT:
      case WorkspaceItemType.APP_ASSESSMENT_VALIDATED:
      case WorkspaceItemType.APP_ASSESSMENT_SNAPSHOT:
        return this.appAssessmentService;

      case WorkspaceItemType.GLOBAL_MEASURE_CHECK:
      case WorkspaceItemType.GLOBAL_MEASURE_CHECK_VALIDATED:
      case WorkspaceItemType.GLOBAL_MEASURE_CHECK_SNAPSHOT:
        return this.globalMeasureChecksService;

      case WorkspaceItemType.TREATMENT_PROJECT:
      case WorkspaceItemType.TREATMENT_HOLDER:
      case WorkspaceItemType.TREATMENT_RESPONSIBLE:
      case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE:
      case WorkspaceItemType.TREATMENT_PROJECT_VALIDATED:
      case WorkspaceItemType.TREATMENT_HOLDER_VALIDATED:
      case WorkspaceItemType.TREATMENT_RESPONSIBLE_VALIDATED:
      case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE_VALIDATED:
      case WorkspaceItemType.TREATMENT_PROJECT_SNAPSHOT:
      case WorkspaceItemType.TREATMENT_HOLDER_SNAPSHOT:
      case WorkspaceItemType.TREATMENT_RESPONSIBLE_SNAPSHOT:
      case WorkspaceItemType.TREATMENT_SUB_RESPONSIBLE_SNAPSHOT:
        return this.treatmentService;

      case WorkspaceItemType.COVID_ASSESSMENT:
      case WorkspaceItemType.COVID_ASSESSMENT_VALIDATED:
      case WorkspaceItemType.COVID_ASSESSMENT_SNAPSHOT:
        return this.covidAssessmentsService;
      default:
        return undefined;
    }
  }

  public getTemplateDocumentTypeService(
    templateDocumentType: TemplateDocumentType
  ) {
    switch (templateDocumentType) {
      case TemplateDocumentType.AI_ASSESSMENT:
        return this.aiAssessmentService;
      case TemplateDocumentType.APP_ASSESSMENT:
        return this.appAssessmentService;
      case TemplateDocumentType.GLOBAL_MEASURE_CHECK:
        return this.globalMeasureChecksService;
      case TemplateDocumentType.TREATMENT_PROJECT:
      case TemplateDocumentType.TREATMENT_HOLDER:
      case TemplateDocumentType.TREATMENT_RESPONSIBLE:
      case TemplateDocumentType.TREATMENT_SUB_RESPONSIBLE:
        return this.treatmentService;
      case TemplateDocumentType.COVID_ASSESSMENT:
        return this.covidAssessmentsService;
      default:
    }
    return undefined;
  }

  public documentDirectDownload(
    docId: number,
    resourceType: WorkspaceItemType
  ): void {
    const service = this.getWorkspaceItemTypeService(resourceType);
    if (service) {
      service
        .downloadPreviewPdf(docId, { chapterSelectors: [] })
        .subscribe((response: DownloadResponse) => {
          FileSaver.saveAs(response.blob, response.fileName + '.pdf');
        });
    }
  }

  public startDownloadJob(
    request: SubmitDownloadRequest,
    resourceType: TemplateDocumentType
  ): Observable<SubmitDownloadResponse> {
    let url = this.endpointV2;
    switch (resourceType) {
      case TemplateDocumentType.TREATMENT_HOLDER:
      case TemplateDocumentType.TREATMENT_PROJECT:
      case TemplateDocumentType.TREATMENT_RESPONSIBLE:
      case TemplateDocumentType.TREATMENT_SUB_RESPONSIBLE:
        url = url + `treatments/latest/submit-download`;
        break;
      case TemplateDocumentType.APP_ASSESSMENT:
        url = url + `app-assessments/latest/submit-download`;
        break;
      case TemplateDocumentType.AI_ASSESSMENT:
        url = url + `ai-assessments/latest/submit-download`;
        break;
      case TemplateDocumentType.DPIA:
        url = url + `dpias/latest/submit-download`;
        break;
      case TemplateDocumentType.GLOBAL_MEASURE_CHECK:
        url = url + `global-measure-checks/latest/submit-download`;
        break;
      case TemplateDocumentType.COVID_ASSESSMENT:
        url = url + `covid-assessments/latest/submit-download`;
        break;
    }
    return this.httpClient.post<SubmitDownloadResponse>(url, request);
  }

  /**
   * @param jobId
   */
  public downloadComplete(jobId: string) {
    const url = this.endpointV1 + `jobs/${jobId}/download`;
    ApiClientUtils.downloadComplete(this.httpClient, url, DownloadFormat.PDF);
  }
}
